import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  return {
    id: obj?.id,
    quoteNumber: obj?.quote_number,
    serviceId: obj?.service_id,
    clientId: obj?.client_id,
    assignedTo: obj?.assigned_to,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    criteria: obj?.criteria || {},
    basePrice: obj?.base_price,
    adjustments: obj?.adjustments || [],
    subtotal: obj?.subtotal,
    taxAmount: obj?.tax_amount,
    discountAmount: obj?.discount_amount,
    totalAmount: obj?.total_amount,
    currency: obj?.currency,
    lineItems: obj?.line_items || [],
    sentAt: obj?.sent_at,
    acceptedAt: obj?.accepted_at,
    invoicedAt: obj?.invoiced_at,
    rejectedAt: obj?.rejected_at,
    expiresAt: obj?.expires_at,
    termsAndConditions: obj?.terms_and_conditions,
    notes: obj?.notes,
    metadata: obj?.metadata,
    createdBy: obj?.created_by,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
  };
};

const toSnakeCase = (obj) => {
  return {
    quote_number: obj?.quoteNumber,
    service_id: obj?.serviceId,
    client_id: obj?.clientId,
    assigned_to: obj?.assignedTo,
    title: obj?.title,
    description: obj?.description,
    status: obj?.status,
    criteria: obj?.criteria,
    base_price: obj?.basePrice,
    adjustments: obj?.adjustments,
    subtotal: obj?.subtotal,
    tax_amount: obj?.taxAmount,
    discount_amount: obj?.discountAmount,
    total_amount: obj?.totalAmount,
    currency: obj?.currency,
    line_items: obj?.lineItems,
    sent_at: obj?.sentAt,
    accepted_at: obj?.acceptedAt,
    invoiced_at: obj?.invoicedAt,
    rejected_at: obj?.rejectedAt,
    expires_at: obj?.expiresAt,
    terms_and_conditions: obj?.termsAndConditions,
    notes: obj?.notes,
    metadata: obj?.metadata,
  };
};

export const quoteService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('quotes')
      ?.select('*, services(title, category), leads(name, email, phone)')
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data?.map(toCamelCase) || [];
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('quotes')
      ?.select('*, services(title, category, base_price), leads(name, email, phone)')
      ?.eq('id', id)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async create(quote) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate quote number
    const { data: quoteNumber } = await supabase?.rpc('generate_quote_number');

    const { data, error } = await supabase
      ?.from('quotes')
      ?.insert({
        ...toSnakeCase(quote),
        quote_number: quoteNumber,
        created_by: user?.id,
      })
      ?.select()
      ?.single();

    if (error) throw error;
    
    // Send quote created email
    const createdQuote = toCamelCase(data);
    try {
      // Fetch client and service details
      const clientData = await supabase?.from('leads')?.select('name, email')?.eq('id', quote?.clientId)?.single();
      const serviceData = await supabase?.from('services')?.select('title')?.eq('id', quote?.serviceId)?.single();
      
      if (clientData?.data?.email) {
        await emailService?.sendQuoteCreatedEmail(
          createdQuote,
          clientData?.data?.email,
          clientData?.data?.name,
          serviceData?.data?.title
        );
      }
    } catch (emailError) {
      console.error('Failed to send quote created email:', emailError);
      // Don't throw - quote was created successfully
    }
    
    return createdQuote;
  },

  async createPublic(quote) {
    // Public quote creation - no authentication required
    // Generate quote number
    const { data: quoteNumber } = await supabase?.rpc('generate_quote_number');

    const { data, error } = await supabase
      ?.from('quotes')
      ?.insert({
        ...toSnakeCase(quote),
        quote_number: quoteNumber,
        created_by: null, // No user for public quotes
      })
      ?.select()
      ?.single();

    if (error) throw error;
    
    // Send quote created email
    const createdQuote = toCamelCase(data);
    try {
      // Fetch client and service details
      const clientData = await supabase?.from('leads')?.select('name, email')?.eq('id', quote?.clientId)?.single();
      const serviceData = await supabase?.from('services')?.select('title')?.eq('id', quote?.serviceId)?.single();
      
      if (clientData?.data?.email) {
        await emailService?.sendQuoteCreatedEmail(
          createdQuote,
          clientData?.data?.email,
          clientData?.data?.name,
          serviceData?.data?.title
        );
      }
    } catch (emailError) {
      console.error('Failed to send quote created email:', emailError);
      // Don't throw - quote was created successfully
    }
    
    return createdQuote;
  },

  async update(id, quote) {
    const { data, error } = await supabase
      ?.from('quotes')?.update(toSnakeCase(quote))?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async updateStatus(id, status) {
    const updateData = { status };
    
    if (status === 'sent') updateData.sent_at = new Date()?.toISOString();
    if (status === 'accepted') updateData.accepted_at = new Date()?.toISOString();
    if (status === 'invoiced') updateData.invoiced_at = new Date()?.toISOString();
    if (status === 'rejected') updateData.rejected_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('quotes')
      ?.update(updateData)
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    
    const updatedQuote = toCamelCase(data);
    
    // Send appropriate email based on status
    try {
      const quoteDetails = await supabase
        ?.from('quotes')
        ?.select('*, services(title), leads(name, email)')
        ?.eq('id', id)
        ?.single();
      
      const clientEmail = quoteDetails?.data?.leads?.email;
      const clientName = quoteDetails?.data?.leads?.name;
      const serviceTitle = quoteDetails?.data?.services?.title;
      
      if (clientEmail) {
        if (status === 'sent') {
          await emailService?.sendQuoteSentEmail(updatedQuote, clientEmail, clientName, serviceTitle);
        } else if (status === 'accepted') {
          await emailService?.sendQuoteAcceptedEmail(updatedQuote, clientEmail, clientName, serviceTitle);
        }
      }
    } catch (emailError) {
      console.error('Failed to send quote status email:', emailError);
      // Don't throw - status was updated successfully
    }
    
    return updatedQuote;
  },

  async delete(id) {
    const { error } = await supabase?.from('quotes')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  calculateDynamicPrice(service, criteria) {
    let basePrice = service?.basePrice || 0;
    const adjustments = [];

    // Surface-based adjustment
    if (criteria?.surface) {
      const surfaceMultiplier = criteria?.surface > 500 ? 1.5 : criteria?.surface > 200 ? 1.2 : 1.0;
      const surfaceAdjustment = basePrice * (surfaceMultiplier - 1);
      if (surfaceAdjustment > 0) {
        adjustments?.push({
          type: 'surface_multiplier',
          description: `Ajustement surface (${criteria?.surface}m²)`,
          amount: surfaceAdjustment,
        });
        basePrice += surfaceAdjustment;
      }
    }

    // Duration-based adjustment
    if (criteria?.duration) {
      const durationMultiplier = criteria?.duration > 12 ? 0.9 : criteria?.duration > 6 ? 0.95 : 1.0;
      const durationAdjustment = basePrice * (1 - durationMultiplier);
      if (durationAdjustment > 0) {
        adjustments?.push({
          type: 'duration_discount',
          description: `Remise durée (${criteria?.duration} mois)`,
          amount: -durationAdjustment,
        });
        basePrice -= durationAdjustment;
      }
    }

    // Complexity-based adjustment
    if (criteria?.complexity) {
      const complexityMultipliers = { low: 0.8, medium: 1.0, high: 1.3 };
      const multiplier = complexityMultipliers?.[criteria?.complexity] || 1.0;
      const complexityAdjustment = basePrice * (multiplier - 1);
      if (complexityAdjustment !== 0) {
        adjustments?.push({
          type: 'complexity_adjustment',
          description: `Ajustement complexité (${criteria?.complexity})`,
          amount: complexityAdjustment,
        });
        basePrice += complexityAdjustment;
      }
    }

    return {
      basePrice: service?.basePrice || 0,
      adjustments,
      subtotal: basePrice,
      totalAmount: basePrice,
    };
  },

  subscribeToChanges(callback) {
    const channel = supabase
      ?.channel('quotes')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quotes' }, (payload) => {
        callback(payload);
      })
      ?.subscribe();

    return () => supabase?.removeChannel(channel);
  },
};